import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import { Button } from './Button';

interface ErrorStateProps {
  /** The sentence: what could not be done. */
  title: string;
  /** A second line, such as the server's reason. */
  description?: string;
  /**
   * Renders "Try again". Left out when asking again would give the same
   * answer, such as a record that does not exist.
   */
  onRetry?: () => void;
  /**
   * A way onward, such as a back link: the caller's element, carrying its own
   * test ID. Shown after the retry when both are given.
   */
  action?: ReactNode;
  /**
   * `<testId>-error` on the alert and `<testId>-retry-button` on the retry.
   * See docs/test-ids.md.
   */
  testId: string;
  /**
   * The alert's own ID when it cannot be `<testId>-error`. Used once, to keep
   * the published `dashboard-error-alert`. Never for a new screen.
   */
  alertTestId?: string;
}

/**
 * The one shape for "could not be loaded": a page, a table or a card whose
 * request failed.
 *
 * An alert, so a screen reader hears it when it appears. The sentence first,
 * the reason if there is one, then the controls in a row under the text:
 * "Try again", and a way onward. Under the text rather than in the alert's
 * right-hand slot, because two buttons do not fit there on a phone and one
 * place is easier to learn than two.
 */
export function ErrorState({
  title,
  description,
  onRetry,
  action,
  testId,
  alertTestId,
}: ErrorStateProps) {
  return (
    <Alert severity="error" role="alert" data-testid={alertTestId ?? `${testId}-error`}>
      {title}
      {description ? (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      ) : null}
      {onRetry || action ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {onRetry ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onRetry}
              data-testid={`${testId}-retry-button`}
            >
              Try again
            </Button>
          ) : null}
          {action}
        </Box>
      ) : null}
    </Alert>
  );
}
